import React, { useEffect, useRef, useState } from "react";
import { Heart, Eye, Send, CheckCircle2, UserPlus, UserCheck, Loader2 } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import StreamEmbed, { PlatformBadge } from "./StreamEmbed";
import ReportButton from "@/components/public/ReportButton";

let _vkey;
function viewerKey() {
  if (_vkey) return _vkey;
  _vkey = localStorage.getItem("vkey");
  if (!_vkey) { _vkey = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("vkey", _vkey); }
  return _vkey;
}

export default function StreamStage({ stream, onStat }) {
  const { user } = useAuth();
  const [live, setLive] = useState({ viewers: stream.viewers, likes: stream.likes, liked: stream.liked });
  const [following, setFollowing] = useState(stream.streamer.follower_count_following || false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = stream.id;
  const onStatRef = useRef(onStat);
  onStatRef.current = onStat;

  useEffect(() => { setLive((p) => ({ ...p, liked: stream.liked })); }, [stream.liked]);

  useEffect(() => {
    let stop = false;
    const beat = async () => {
      try {
        const r = await api.post(`/streams/${sid}/heartbeat`, { viewer_key: viewerKey() });
        if (!stop) { setLive((p) => ({ ...p, viewers: r.data.viewers, likes: r.data.likes })); onStatRef.current?.(sid, r.data); }
      } catch { /* ignore */ }
    };
    const loadC = async () => {
      try { const r = await api.get(`/streams/${sid}/comments`); if (!stop) setComments(r.data.items); } catch { /* ignore */ }
    };
    beat(); loadC();
    const t = setInterval(() => { beat(); loadC(); }, 8000);
    return () => { stop = true; clearInterval(t); };
  }, [sid]);

  useEffect(() => {
    if (!user) { setFollowing(false); return; }
    api.get(`/members/${stream.user_id}`).then((r) => setFollowing(r.data.is_following)).catch(() => {});
  }, [stream.user_id, user]);

  const like = async () => {
    if (!user) return toast.error("Sign in to like");
    try { const r = await api.post(`/streams/${sid}/like`); setLive((p) => ({ ...p, liked: r.data.liked, likes: r.data.likes })); }
    catch { toast.error("Could not like"); }
  };
  const toggleFollow = async () => {
    if (!user) return toast.error("Sign in to follow");
    try {
      const r = following ? await api.delete(`/members/${stream.user_id}/follow`) : await api.post(`/members/${stream.user_id}/follow`);
      setFollowing(r.data.following);
    } catch { toast.error("Action failed"); }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to comment");
    if (!text.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/streams/${sid}/comments`, { text: text.trim() }); setComments((c) => [r.data, ...c]); setText(""); }
    catch { toast.error("Could not send"); }
    finally { setBusy(false); }
  };

  const s = stream.streamer;
  return (
    <div className="grid lg:grid-cols-[1fr_300px] gap-5" data-testid="stream-stage">
      <div>
        <div className="relative aspect-video rounded-2xl overflow-hidden bg-black border border-white/10">
          <StreamEmbed stream={stream} />
          <div className="absolute top-3 left-3 flex items-center gap-2 pointer-events-none">
            <PlatformBadge platform={stream.platform} />
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-600 text-white text-[11px] font-bold uppercase live-dot">● Live</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/70 text-white text-[11px] font-bold"><Eye size={12} /> {live.viewers.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="flex items-center gap-3">
          {s.avatar ? <img src={mediaUrl(s.avatar)} alt="" className="w-12 h-12 rounded-full object-cover border border-white/10" />
            : <span className="w-12 h-12 rounded-full bg-[hsl(var(--primary))] grid place-items-center font-black text-[hsl(var(--primary-foreground))]">{(s.nickname || s.name || "?")[0]}</span>}
          <div className="min-w-0">
            <div className="font-bold text-white flex items-center gap-1 truncate" data-testid="stage-streamer">{s.nickname || s.name}{s.verified && <CheckCircle2 size={15} className="text-[hsl(var(--primary))]" />}</div>
            <div className="text-xs text-white/50 uppercase tracking-wide flex items-center gap-2">{stream.game}{s.channel_verified && <span data-testid="stage-channel-verified" className="inline-flex items-center gap-1 normal-case tracking-normal px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] text-[10px] font-bold"><CheckCircle2 size={10} /> Verified channel</span>}</div>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          {user?.user_id !== stream.user_id && <button onClick={toggleFollow} data-testid="stage-follow" className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-bold transition ${following ? "bg-white/10 text-white" : "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"}`}>
            {following ? <UserCheck size={15} /> : <UserPlus size={15} />} {following ? "Following" : "Follow"}
          </button>}
          <button onClick={like} data-testid="stage-like" className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold border transition ${live.liked ? "bg-red-500/15 border-red-500/40 text-red-400" : "border-white/15 text-white/70 hover:text-white"}`}>
            <Heart size={15} fill={live.liked ? "currentColor" : "none"} /> {live.likes.toLocaleString()}
          </button>
        </div>
        <div className="mt-3 flex items-start justify-between gap-2">
          <h2 className="text-sm font-semibold text-white">{stream.title}</h2>
          <ReportButton type="stream" id={sid} ownerId={stream.user_id} className="shrink-0 text-xs text-white/40" />
        </div>
        {stream.tags?.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{stream.tags.map((t) => <span key={t} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white/60">{t}</span>)}</div>}

        <div className="mt-4 flex-1 card-surface rounded-2xl flex flex-col min-h-[240px] max-h-[360px]">
          <div className="px-3 py-2 border-b border-white/5 text-xs font-semibold text-white/60 uppercase tracking-wide">Live Chat</div>
          <div className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-2.5" data-testid="stream-comments">
            {comments.length === 0 && <p className="text-xs text-white/30">No messages yet. Say hi!</p>}
            {comments.map((c) => (
              <div key={c.id} className="group text-sm"><span className="font-semibold text-[hsl(var(--primary))]">{c.name}</span> <span className="text-white/80">{c.text}</span>
                <ReportButton type="stream_comment" id={c.id} ownerId={c.user_id} iconOnly className="ml-1.5 align-middle text-white/30 opacity-60 group-hover:opacity-100" /></div>
            ))}
          </div>
          <form onSubmit={send} className="p-2 border-t border-white/5 flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={user ? "Send a message…" : "Sign in to chat"} disabled={!user}
              data-testid="stream-comment-input" className="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]" />
            <button disabled={busy || !user} data-testid="stream-comment-send" className="p-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
          </form>
        </div>
        <p className="mt-2 text-[11px] text-white/30">Chat is live only — messages clear when the stream ends.</p>
      </div>
    </div>
  );
}
